import { useState, useEffect, useCallback, memo } from 'react'
import { useParams } from 'react-router-dom'
import { Edit2, Check, AlertTriangle } from 'lucide-react'
import { getUserById, getUserVehicles } from '../services/userService.js'
import { updateVehicle } from '../services/vehicleService.js'
import { useToast } from '../components/Toast.jsx'
import Pagination from '../components/Pagination.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import Breadcrumbs from '../components/Breadcrumbs.jsx'
import { statusColors, STATUS_OPTIONS, DEFAULT_PAGE_SIZE } from '../constants/statusColors.js'
import { validateVehicleForm, hasErrors } from '../utils/validation.js'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const VehicleRow = memo(({ v, onEdit }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
    <td className="px-6 py-4"><span className="font-mono font-semibold text-sm">{v.plate}</span> <span className="text-xs text-gray-400">{v.plateCountry}</span></td>
    <td className="px-6 py-4 font-mono text-xs text-gray-400">{v.vin || '-'}</td>
    <td className="px-6 py-4 text-sm">{[v.brandName, v.modelName].filter(Boolean).join(' ') || '-'}</td>
    <td className="px-6 py-4 text-center text-sm">{v.productionYear || '-'}</td>
    <td className="px-6 py-4 text-center text-xs">{v.fuelType || '-'}</td>
    <td className="px-6 py-4 text-center">
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[v.status] || 'bg-gray-500/20 text-gray-400'}`}>{v.status}</span>
    </td>
    <td className="px-6 py-4 text-right text-sm">{v.mileage != null ? v.mileage.toLocaleString() + ' km' : '-'}</td>
    <td className="px-6 py-4 text-right">
      <button onClick={() => onEdit(v)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={`Edit vehicle ${v.plate}`}>
        <Edit2 className="w-4 h-4 text-gray-400" />
      </button>
    </td>
  </tr>
))
VehicleRow.displayName = 'VehicleRow'

const UserVehiclesPage = () => {
  const { userId } = useParams()
  const [user, setUser] = useState(null)
  const [userError, setUserError] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editVehicle, setEditVehicle] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const toast = useToast()

  useEffect(() => {
    getUserById(userId)
      .then((res) => setUser(res.data))
      .catch(() => setUserError('Failed to load user details'))
  }, [userId])

  const fetchVehicles = useCallback(() => {
    setLoading(true)
    setError(null)
    getUserVehicles(userId, page, DEFAULT_PAGE_SIZE)
      .then((res) => {
        const data = res.data
        setVehicles(data.content || [])
        setTotalPages(data.totalPages || 0)
        setTotalElements(data.totalElements || 0)
      })
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => setLoading(false))
  }, [userId, page])

  useEffect(() => { fetchVehicles() }, [fetchVehicles])

  const openEdit = useCallback((v) => {
    setEditVehicle(v)
    setEditForm({ plate: v.plate || '', status: v.status || 'ACTIVE', mileage: v.mileage ?? '', notes: v.notes || '' })
    setFormErrors({})
    setSaveError(null)
    setFormDirty(false)
  }, [])

  const handleCloseModal = useCallback(() => {
    if (formDirty) {
      setConfirmClose(true)
    } else {
      setEditVehicle(null)
    }
  }, [formDirty])

  const updateField = useCallback((field, value) => {
    setEditForm(f => ({ ...f, [field]: value }))
    setFormDirty(true)
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }, [])

  const saveEdit = async () => {
    const errors = validateVehicleForm(editForm)
    if (hasErrors(errors)) {
      setFormErrors(errors)
      return
    }
    if (!editVehicle) return
    setSaving(true)
    setSaveError(null)
    try {
      const data = { ...editForm }
      if (data.mileage === '') delete data.mileage
      else data.mileage = Number(data.mileage)
      await updateVehicle(editVehicle.vehicleUuid, data)
      setEditVehicle(null)
      toast.success('Vehicle updated successfully')
      fetchVehicles()
    } catch {
      setSaveError('Failed to update vehicle. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbs = [
    { label: 'Users', to: '/users' },
    { label: user?.name || user?.email || 'User' },
    { label: 'Vehicles' },
  ]

  return (
    <div className="space-y-4">
      <Breadcrumbs items={breadcrumbs} />

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold">{user?.name || user?.email || 'Loading...'}</h2>
        {user && <p className="text-sm text-gray-400">{user.email} &middot; {totalElements} vehicles</p>}
        {userError && <p className="text-sm text-red-400 mt-1">{userError}</p>}
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchVehicles} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Plate</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">VIN</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Brand / Model</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Year</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Fuel</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Mileage</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : vehicles.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">This user has no vehicles</td></tr>
            ) : (
              vehicles.map((v) => <VehicleRow key={v.vehicleUuid} v={v} onEdit={openEdit} />)
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} label="vehicles" />

      {/* Edit Modal */}
      <Modal open={!!editVehicle} title="Edit Vehicle" onClose={handleCloseModal} footer={
        <>
          <button onClick={handleCloseModal} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={saveEdit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {saveError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{saveError}</div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Plate" htmlFor="edit-uv-plate" error={formErrors.plate}>
            <input id="edit-uv-plate" type="text" value={editForm.plate || ''} onChange={(e) => updateField('plate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="Status" htmlFor="edit-uv-status">
            <select id="edit-uv-status" value={editForm.status || ''} onChange={(e) => updateField('status', e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Mileage (km)" htmlFor="edit-uv-mileage" error={formErrors.mileage}>
          <input id="edit-uv-mileage" type="number" min="0" value={editForm.mileage ?? ''} onChange={(e) => updateField('mileage', e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Notes" htmlFor="edit-uv-notes">
          <textarea id="edit-uv-notes" value={editForm.notes || ''} onChange={(e) => updateField('notes', e.target.value)} rows={3} className={`${inputCls} resize-none`} />
        </FormField>
      </Modal>

      <ConfirmDialog
        open={confirmClose}
        title="Unsaved changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmLabel="Discard"
        onConfirm={() => { setConfirmClose(false); setEditVehicle(null) }}
        onCancel={() => setConfirmClose(false)}
      />
    </div>
  )
}

export default UserVehiclesPage
